import React, { PureComponent } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import objectPath from 'object-path';
import screen from 'superior-mq';
import { rem } from 'polished';
import { bp } from 'Styles/helpers';
import Section from '../../../section';
import CarouselWrap from '../../../carousel/CarouselWrap';
import UnstyledList from '../../../unstyled-list';
import UnstyledButton from '../../../unstyled-button';
import TextWave from '../../../text-wave';
import MdLink from '../../../render-md/md-link';

export default class HistoryCarousel extends PureComponent {

  static propTypes = {
    children: PropTypes.node,
    id: PropTypes.string,
  }

  state = {
    opacity: 0,
    isReady: false,
    active: 0,
    activeSlideYear: this.props.slideDates[0],
  }

  componentDidMount() {
    const Flickity = require('flickity');

    this.slider = new Flickity(this.flickityNode, {
      cellAlign: 'center',
      prevNextButtons: false,
      pageDots: false,
      wrapAround: true,
      percentPosition: true,
      imagesLoaded: true,
      ...this.props.settings,
    });

    this.setReady();

    this.slider.on('change', this.updateActive);
  }

  setReady = () => {
    this.setState({
      isReady: true,
      opacity: 1,
    });
  }

  updateActive = (active) => {
    this.setState({
      active,
      activeSlideYear: this.props.slideDates[active],
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const activeChange = !prevState.isReady && this.state.isReady;
    const slidesChange = prevProps.children.length !== this.props.children.length;

    if (activeChange || slidesChange) {
      this.updateFlickity();
    }
  }

  updateFlickity = () => {
    const initial = (this.props.settings && this.props.settings.initialIndex) || 0;

    this.slider.reloadCells();
    this.slider.resize();
    this.slider.updateDraggable();
    this.slider.select(initial, false, true);

    this.updateActive(initial);
  }

  renderPortal = () => {
    if (!this.flickityNode) {
      return null;
    }

    const mountNode = this.flickityNode.querySelector('.flickity-slider');
    const childWithProp = React.Children.map(this.props.children, (child, i) => (
      React.cloneElement(child, {
        index: i,
        activeSlide: this.state.active,
        length: this.props.children.length,
      })
    ));

    if (mountNode) {
      return createPortal(childWithProp, mountNode);
    }
  }

  renderSlideNav = () => Array.from([...new Set(this.props.slideDates)]);

  changeSlide = (e) => {
    const year = parseInt(e.target.innerText, 10);
    const newSlide = this.props.slideDates.indexOf(year);

    this.slider.select(newSlide, true);
  }

  render() {
    return (
      <HistorySection dark id={this.props.id}>
        <TextWave>
          <h2>History</h2>
        </TextWave>
        <CarouselContainer>

          {[
            <CarouselWrap
              key="carousel-slider"
              ref={(node) => { this.flickityNode = node; }}
              opacity={this.state.opacity}
              styles={this.props.styles}
            />,
            this.renderPortal(),
          ].filter(Boolean)
          }

          {objectPath.has(this.props.slides[this.state.active], 'fields.eventDescription') &&
            <MobileCaption>
              <MdLink source={this.props.slides[this.state.active].fields.eventDescription} />
            </MobileCaption>
          }

        </CarouselContainer>

        <CarouselNav>
          <UnstyledList>
            {this.renderSlideNav().map((year, i) => (
              <SlideNavItem
                key={i} // eslint-disable-line react/no-array-index-key
                aria-current={year === this.state.activeSlideYear}
              >
                <UnstyledButton onClick={this.changeSlide}>
                  {year}
                </UnstyledButton>
              </SlideNavItem>
            ))}
          </UnstyledList>
        </CarouselNav>

      </HistorySection>
    );
  }
}

const CarouselContainer = styled.div`
  position: relative;
  overflow: hidden;
  margin-top: 80px;

  ${screen.below(bp.mobile, `
    margin-top: 40px;
  `)}

  *:focus {
    outline: none;
  }

  img {
    display: block;
    pointer-events: none;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${props => props.styles && css`
    ${props.styles}
  `}
`;

const HistorySection = styled(Section)`
  text-align: center;
`;

const MobileCaption = styled.div`
  width: 400px;
  margin: 40px auto 0;
  font-size: 1rem;

  ${screen.below(bp.mobileSm, `
    width: 50%;
    font-size: ${rem(11)};
    text-align: left;
  `)}

  ${screen.above(bp.portrait, `
    display: none;
  `)}
`;

const CarouselNav = styled.nav`
  ${screen.below(bp.portrait, `
    display: none;
  `)}
`;

const SlideNavItem = styled.li`
  display: inline-block;
  margin-top: 30px;

  &:not(:first-of-type) {
    margin-left: 20px;
  }

  button {
    font-size: 1rem;
    color: ${props => props.theme.gray};
    outline: none;
  }

  &[aria-current="true"] button {
    color: #fff;
  }
`;
